import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { GetContactQuery } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { usePathname } from 'next/navigation'

export type MenuCategoryItemType = {
  active: boolean
  pageName: string
  title: string
  items: MenuItemType[]
}

export type MenuItemType = {
  active: boolean
  pageName: string
  title: string
}

type ContactType = NonNullable<GetContactQuery['contact']>
type VisibilityType = ContactType['visibility']

type UseMenuItemsProps = {
  visibility: VisibilityType
}

const useMenuItems = ({ visibility }: UseMenuItemsProps) => {
  const { translate } = useTranslations()
  const pathname = usePathname()

  const orderItems: MenuItemType[] = [
    {
      pageName: '/orders',
      title: translate('OrderHistory_OrderList'),
      active: pathname === '/orders',
    },
    {
      pageName: '/orders/products',
      title: translate('OrderHistory_PurchasedProducts'),
      active: pathname === '/orders/products',
    },
    {
      pageName: '/orders/invoices',
      title: translate('OrderHistory_Invoices'),
      active: pathname === '/orders/invoices',
    },
  ]

  if (visibility?.showExternalOrders) {
    orderItems.push({
      pageName: '/orders/external-orders',
      title: translate('OrderHistory_ExternalOrderList'),
      active: pathname === '/orders/external-orders',
    })
  }

  const accountItems: MenuItemType[] = [
    {
      pageName: '/account/myaccount',
      title: translate('AccountSettings'),
      active: pathname === '/account/myaccount',
    },
    {
      pageName: '/account/changepassword',
      title: translate('ChangePassword'),
      active: pathname === '/account/changepassword',
    },
  ]

  const categoryItems: MenuCategoryItemType[] = [
    {
      active: pathname === '/account/overview',
      title: translate('Overview_heading'),
      pageName: '/account/overview',
      items: [],
    },
    {
      active: orderItems.some(o => o.pageName === pathname),
      title: translate('OrderHistory'),
      pageName: '/orders',
      items: orderItems,
    },
    {
      active: pathname === '/rma',
      title: translate('MattersAndComplaints'),
      pageName: '/rma',
      items: [],
    },
    {
      active: accountItems.some(o => o.pageName === pathname),
      title: translate('AccountSettings'),
      pageName: '/account/myaccount',
      items: accountItems,
    },
  ]

  if (visibility?.showAttest) {
    categoryItems.push({
      active: pathname === '/preorder',
      title: translate('PreOrders_Title'),
      pageName: '/preorder',
      items: [],
    })
  }

  if (visibility?.showAddresses) {
    categoryItems.push({
      active: pathname === '/account/addresses',
      title: translate('ManageAddresses'),
      pageName: '/account/addresses',
      items: [],
    })
  }

  if (visibility?.showContacts) {
    categoryItems.push({
      active: pathname === '/account/contacts',
      title: translate('ManageContacts'),
      pageName: '/account/contacts',
      items: [],
    })
  }

  if (visibility?.showAttest) {
    categoryItems.push({
      active: pathname === '/account/attestsetup',
      title: translate('AttestSetup'),
      pageName: '/account/attestsetup',
      items: [],
    })
  }

  if (visibility?.showServices) {
    categoryItems.push({
      active: pathname === '/account/services',
      title: translate('Services_Heading'),
      pageName: '/account/services',
      items: [],
    })
  }

  return { categoryItems }
}

export default useMenuItems
